import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!token && !!user;

  // Check if token is expired
  const isTokenExpired = useCallback((token) => {
    if (!token) return true;
    try {
      const decoded = jwtDecode(token);
      return decoded.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  }, []);

  // Logout function
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete api.defaults.headers.common['Authorization'];
  }, []);

  // Fetch user data
  const fetchUser = useCallback(async () => {
    try {
      const response = await api.get('/user');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    }
  }, [logout]);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      if (token && !isTokenExpired(token)) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        await fetchUser();
      } else if (token) {
        // Token expired, logout
        logout();
      }
      setIsLoading(false);
    };

    initAuth();
  }, [token, isTokenExpired, fetchUser, logout]);

  // Login function
  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { token: newToken, user: userData } = response.data;

    localStorage.setItem('token', newToken);
    api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);

    return userData;
  };

  // Register function
  const register = async (email, password, firstname = null) => {
    const response = await api.post('/auth/register', { email, password, firstname });
    return response.data;
  };

  // Update user profile
  const updateUser = async (data) => {
    const response = await api.put('/user', data);
    setUser(response.data);
    return response.data;
  };

  // Change password
  const changePassword = async (currentPassword, newPassword) => {
    await api.put('/user/password', { currentPassword, newPassword });
  };

  // Delete account
  const deleteAccount = async () => {
    await api.delete('/user');
    logout();
  };

  // Request password reset
  const forgotPassword = async (email) => {
    await api.post('/auth/forgot-password', { email });
  };

  // Reset password with token
  const resetPassword = async (resetToken, newPassword) => {
    await api.post('/auth/reset-password', { token: resetToken, password: newPassword });
  };

  const value = {
    user,
    token,
    isAuthenticated,
    isLoading,
    login,
    logout,
    register,
    updateUser,
    changePassword,
    deleteAccount,
    forgotPassword,
    resetPassword,
    refreshUser: fetchUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
